import { gql, useQuery } from "@apollo/client";

// KST(UTC+9) 기준 오늘 날짜 문자열 반환
function getKstToday(): string {
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().slice(0, 10);
}

// KST 기준 N일 전 날짜 문자열 반환
function getKstDateBefore(days: number): string {
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    kst.setDate(kst.getDate() - days);
    return kst.toISOString().slice(0, 10);
}

const USER_READING_STREAK = gql`
    query UserReadingStreak($userId: uuid!, $since: timestamp!) {
        mix_user_streak(where: { user_id: { _eq: $userId } }) {
            current_streak
            longest_streak
            last_read_date
            total_read_days
        }
        # 최근 N일 읽기 활동 (도트 표시용)
        mix_content_history(
            where: {
                userid: { _eq: $userId }
                updatetime: { _gte: $since }
            }
        ) {
            updatetime
        }
    }
`;

/**
 * 유저의 daily reading streak + 최근 9일 활동 데이터를 조회하는 훅.
 * userId가 없으면 쿼리를 건너뜀.
 */
export const useReadingStreak = (userId: string | undefined | null) => {
    // 3일 전 KST 자정을 UTC로 환산 (timestamp 컬럼은 timezone 없이 UTC 저장)
    const since = getKstDateBefore(3) + "T15:00:00";

    const { data, loading } = useQuery<{
        mix_user_streak: Array<{
            current_streak: number;
            longest_streak: number;
            last_read_date: string | null;
            total_read_days: number;
        }>;
        mix_content_history: Array<{ updatetime: string }>;
    }>(USER_READING_STREAK, {
        variables: { userId, since },
        skip: !userId,
        fetchPolicy: "cache-and-network"
    });

    const streak = data?.mix_user_streak?.[0];

    // 최근 N일 읽은 날짜 집합 계산 (KST 기준, 도트 표시용)
    const readDatesSet = new Set<string>();
    (data?.mix_content_history ?? []).forEach(({ updatetime }) => {
        const kst = new Date(new Date(updatetime).getTime() + 9 * 60 * 60 * 1000);
        readDatesSet.add(kst.toISOString().slice(0, 10));
    });

    // 총 읽은 날 수: 스트릭 테이블에서 직접 조회 (정확한 값)
    const totalReadDays = streak?.total_read_days ?? 0;

    const today = getKstToday();
    // 오늘을 중앙(index 3)에 배치: 3일 전 ~ 오늘 ~ 3일 후
    const recentDays = Array.from({ length: 7 }, (_, i) => getKstDateBefore(3 - i));

    return {
        currentStreak: streak?.current_streak ?? 0,
        longestStreak: streak?.longest_streak ?? 0,
        lastReadDate: streak?.last_read_date ?? null,
        recentDays,    // ["2026-03-20", ..., "2026-03-26"] (최근 7일)
        readDatesSet,  // 최근 N일 읽은 날짜 집합 (도트용)
        totalReadDays, // 90일 범위 고유 읽은 날 수
        today,
        loading
    };
};
